import {
  Component, Input, ViewEncapsulation, Output, EventEmitter
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { DatePipe } from '@angular/common';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';
import groupBy from 'lodash-es/groupBy';

import { isNullOrUndefined } from '@app/util/util';
import { HotelReservation, ReservedRoom } from '@booking/entities/hotel-validation.response';
import { hotelReservationGuestNumber, totalNights } from '@ux/mappers/guest.mapper';
import { DateTransformPipe } from '@app/pipes/date-format.pipe';
import { PlaceNamePipe } from '@app/pipes/place-name.pipe';
import { PlaceSelectors } from '@app/entities/place';
import { extractHour, formatStrinWithTimeZoneDateByRegex } from '@ux/mappers/date.mapper';
import { ReservationType } from '@user-history/entities/user-history-item';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';

@Component({
  selector: 'app-hotel-segment-detail',
  templateUrl: './hotel-segment-detail.component.html',
  styleUrls: ['./hotel-segment-detail.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: false
})
export class HotelSegmentDetailComponent {

  @Input() hotelReservation: HotelReservation;
  @Input() isPackageResume: boolean;
  @Input() isPackageResumeStep: boolean;
  @Input() isPackageBooking: boolean;
  @Input() showRoomsDetail: boolean;
  @Input() isAdandonedHotelReservation: boolean;
  @Input() isAdandonedPackageReservation: boolean;
  @Input() capacity: number;

  @Output() showHotelDetail: EventEmitter<void> = new EventEmitter<void>();

  public readonly icons = Icon;
  public readonly iconFillColor = IconFillColor;

  private dateFormat: string = 'd MMM';
  private dateFormatWithWeekDay: string = 'EE, d MMM';
  private placeCriteria: string = PlaceSelectors.City;

  public roomString: string = this.getTranslate('HOTELS_MODULE.DETAIL_DESCRIPTION.ROOM_DETAIL') as string;
  public roomsString: string = this.getTranslate('HOTELS_MODULE.DETAIL_DESCRIPTION.ROOMS_DETAIL') as string;

  constructor(
    private datePipe: DatePipe,
    private placeNamePipe: PlaceNamePipe,
    private dateTransformPipe: DateTransformPipe,
    private translate: TranslocoService,
  ) {
  }

  get isAdandonedReservation(): boolean {
    return this.isAdandonedHotelReservation || this.isAdandonedPackageReservation;
  }

  get isPackageTemplate(): boolean {
    return !!this.isPackageResume || !!this.isPackageBooking;
  }

  get showCompleteData(): boolean {
    return !this.isPackageTemplate && !this.isPackageResumeStep && !this.isAdandonedReservation;
  }

  get guest(): number {
    return this.isByhours 
      ? this.capacity
      : hotelReservationGuestNumber(this.hotelReservation);
  }

  get nights(): number {
    return this.hotelReservation ? totalNights(this.hotelReservation.Checkout, this.hotelReservation.Checkin) : 0;
  }

  get isOneNight(): boolean {
    return !!this.nights && this.nights === 1;
  }

  get rooms(): number {
    return this.hotelReservation.Rooms.length;
  }

  get hasRoomInfo(): boolean {
    return !!this.rooms && !!this.showRoomsDetail &&
      (!!this.isPackageBooking || !!this.isAdandonedReservation) && this.isPackageResume;
  }

  get roomsDetail(): string[] {
    const groupByCode = groupBy(this.hotelReservation.Rooms, 'RoomCode');
    if (!isEmpty(groupByCode)) {
      const collection = [];
      for (const code in groupByCode) {
        if (Object.prototype.hasOwnProperty.call(groupByCode, code)) {
          const rooms: ReservedRoom[] = groupByCode[code];
          const quantity = rooms.length;
          const roomLabel = quantity === 1 ? this.roomString : this.roomsString;
          collection.push(`${quantity} x ${roomLabel} ${rooms?.[0]?.Description}`)
        }
      }
      return collection;
    } else return [];
  }

  get hasCategory(): boolean {
    return !!this.hotelReservation && !!this.hotelReservation.Hotel && !!this.hotelReservation.Hotel.Category;
  }

  get stars(): number[] {
    return this.hasCategory ? [].constructor(this.hotelReservation.Hotel.Category) : [];
  }

  get checkIn(): string {
    if (!isNullOrUndefined(this.hotelReservation.Checkin)) {
      return this.formatDate(new Date(formatStrinWithTimeZoneDateByRegex(this.hotelReservation.Checkin)));
    }
  }

  get checkOut(): string {
    if (!isNullOrUndefined(this.hotelReservation.Checkout)) {
      return this.formatDate(new Date(formatStrinWithTimeZoneDateByRegex(this.hotelReservation.Checkout)));
    }
  }

  get destinationName$(): Observable<string> {
    const name = this.hotelReservation.DestinationName;
    return !isNullOrUndefined(name) && !isEmpty(name) && !this.isPackageResume && !this.isPackageResumeStep ? of(name) :
      this.placeNamePipe.transform(name, this.placeCriteria).pipe(map(city => city));
  }

  get isByhours() : boolean {
    return this.hotelReservation?.MicroStay && this.hotelReservation?.ReservationType === ReservationType.Hotel;
  }

  get packHour() : number {
    return this.hotelReservation?.Rooms[0]?.PackHour;
  }

  get checkInHour(): string {
    return !isNullOrUndefined(this.hotelReservation.Checkin) ? extractHour(this.hotelReservation.Checkin) : '';
  }

  public trackById(index: number): number {
    return index;
  }

  private formatDate(date: Date): string {
    const formatType = this.isAdandonedReservation ? this.dateFormatWithWeekDay : this.dateFormat;
    return this.formatStringDate(this.datePipe.transform(date, formatType));
  }

  private formatStringDate(date: string): string {
    return this.dateTransformPipe.transform(date);
  }

  private getTranslate(key: string | string[]): string | string[] {
    return this.translate.translate(key);
  }

}
